import type { ProjectOverviewItemDto } from "@/api/types";
import { isDefined } from "@/helpers/util";

export function optionalNumberSort(a: undefined | number, b: undefined | number): number {
  if (!isDefined(b)) {
    return 1;
  }

  if (!isDefined(a)) {
    return -1;
  }

  return a - b;
}

function calculateTrendPercentage(value: number, previousValue: number): number | undefined {
  if (previousValue === 0) {
    return undefined;
  }

  return Math.round(((value - previousValue) / previousValue) * 100);
}

interface Stat {
  value: number | undefined;
  changePercentage: number | undefined;
}

interface Stats {
  feelingAtHomeRating: {
    periodic: Stat;
    live: Stat;
  };
  averageTicketRating: {
    periodic: Stat;
    live: Stat;
  };
  engagedResidentPercentage: {
    periodic: Stat;
    live: Stat;
  };
  adoptionPercentage: {
    periodic: Stat;
    live: Stat;
  };
}

export function calculateStats(project: ProjectOverviewItemDto): Stats {
  return {
    feelingAtHomeRating: {
      periodic: {
        value: project.stats?.feelingAtHomeAverageRatingAllTime,
        changePercentage:
          project.stats?.feelingAtHomeAverageRatingAllTime && project.previousStats?.feelingAtHomeAverageRatingAllTime
            ? calculateTrendPercentage(
                project.stats.feelingAtHomeAverageRatingAllTime,
                project.previousStats.feelingAtHomeAverageRatingAllTime,
              )
            : undefined,
      },
      live: {
        value: project.liveStats?.feelingAtHomeAverageRatingAllTime,
        changePercentage:
          project.liveStats?.feelingAtHomeAverageRatingAllTime && project.stats?.feelingAtHomeAverageRatingAllTime
            ? calculateTrendPercentage(
                project.liveStats.feelingAtHomeAverageRatingAllTime,
                project.stats.feelingAtHomeAverageRatingAllTime,
              )
            : undefined,
      },
    },
    averageTicketRating: {
      periodic: {
        value: project.stats?.averageTicketRating,
        changePercentage:
          project.stats?.averageTicketRating && project.previousStats?.averageTicketRating
            ? calculateTrendPercentage(project.stats.averageTicketRating, project.previousStats.averageTicketRating)
            : undefined,
      },
      live: {
        value: project.liveStats?.averageTicketRating,
        changePercentage:
          project.liveStats?.averageTicketRating && project.stats?.averageTicketRating
            ? calculateTrendPercentage(project.liveStats.averageTicketRating, project.stats.averageTicketRating)
            : undefined,
      },
    },
    engagedResidentPercentage: {
      periodic: {
        value: project.stats?.engagedResidentPercentage,
        changePercentage:
          project.stats?.engagedResidentPercentage && project.previousStats?.engagedResidentPercentage
            ? project.stats.engagedResidentPercentage - project.previousStats.engagedResidentPercentage
            : undefined,
      },
      live: {
        value: project.liveStats?.engagedResidentPercentage,
        changePercentage:
          project.liveStats?.engagedResidentPercentage && project.stats?.engagedResidentPercentage
            ? project.liveStats.engagedResidentPercentage - project.stats.engagedResidentPercentage
            : undefined,
      },
    },
    adoptionPercentage: {
      periodic: {
        value: project.stats?.adoptionPercentage,
        changePercentage:
          project.stats?.adoptionPercentage && project.previousStats?.adoptionPercentage
            ? project.stats.adoptionPercentage - project.previousStats.adoptionPercentage
            : undefined,
      },
      live: {
        value: project.liveStats?.adoptionPercentage,
        changePercentage:
          project.liveStats?.adoptionPercentage && project.stats?.adoptionPercentage
            ? project.liveStats.adoptionPercentage - project.stats.adoptionPercentage
            : undefined,
      },
    },
  };
}
